import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "Invest With Us / Property Owner",
  "Property Sale",
  "Lender / Financing",
  "Builder / Developer",
  "Professional / Consultant",
  "General Inquiry",
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", category: categories[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact | Stelcore Development";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      await axios.post(`${API}/contact-inquiries`, data);
      setSent(true);
      toast.success("Your inquiry has been sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-ink/10">
        <div className="container-x py-20 md:py-28">
          <Reveal>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-bold uppercase" data-testid="contact-headline">
              Let's discuss an opportunity.
            </h1>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="container-x py-16 md:py-24">
          <div className="max-w-3xl mx-auto">
            <Reveal delay={0.1}>
              {sent ? (
                <div className="border border-ink/15 p-10 md:p-14 text-center" data-testid="contact-success">
                  <span className="inline-flex items-center justify-center w-14 h-14 border border-ink mb-6">
                    <Check size={22} />
                  </span>
                  <h3 className="text-2xl font-semibold uppercase tracking-tight mb-4">Inquiry Sent</h3>
                  <p className="text-sm text-graphite">Thank you for reaching out. Stelcore Development will respond as appropriate.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="border border-ink/15 p-8 md:p-12 space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">Name <span className="text-taupe">*</span></label>
                      <input id="name" className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-input-name" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">Email <span className="text-taupe">*</span></label>
                      <input id="email" type="email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-input-email" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">Inquiry Category</label>
                    <select id="category" className="input-field bg-bone" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="contact-select-category">
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">Message</label>
                    <textarea id="message" rows={6} className="input-field resize-y" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="contact-submit-button">
                    {submitting ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : "Send Inquiry"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
